// Formats de nombres, de montants et de dates selon la langue (Intl) : jamais de format écrit à la
// main dans un composant. « 12,34 € » en français, « €12.34 » en anglais, « 12,34 € » en allemand…
const cache = new Map();
const memo = (Ctor, locale, options) => {
  const key = `${Ctor.name}|${locale}|${JSON.stringify(options || {})}`;
  if (!cache.has(key)) cache.set(key, new Ctor(locale, options));
  return cache.get(key);
};

// « 2026-09-19 » (date seule) est lue comme une date LOCALE : `new Date('2026-09-19')` serait minuit UTC
// et afficherait la veille dans certains fuseaux.
const toDate = (value) => {
  if (value instanceof Date) return value;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(value);
};

export const makeFormat = (locale) => {
  const number = (n, options) => memo(Intl.NumberFormat, locale, options).format(Number(n));
  const money = (n, { currency = 'EUR', ...options } = {}) => number(n, { style: 'currency', currency, ...options });
  const moneyCents = (cents, options) => money(Number(cents) / 100, options);
  const percent = (n, options) => number(n, { style: 'percent', maximumFractionDigits: 1, ...options });
  const date = (value, style = 'medium') => memo(Intl.DateTimeFormat, locale, { dateStyle: style }).format(toDate(value));
  const dateTime = (value, options = { dateStyle: 'medium', timeStyle: 'short' }) => memo(Intl.DateTimeFormat, locale, options).format(toDate(value));
  const list = (items, type = 'conjunction') => memo(Intl.ListFormat, locale, { style: 'long', type }).format(items);
  // Appelé par la syntaxe {{valeur, format}} des textes : number, currency (ou currency:USD), percent,
  // date (ou date:long), dateLong.
  const apply = (name, value, arg) => {
    switch (name) {
      case 'number': return number(value);
      case 'currency': case 'money': return money(value, arg ? { currency: arg } : undefined);
      case 'percent': return percent(value);
      case 'date': return date(value, arg || 'medium');
      case 'dateLong': return date(value, 'long');
      default: return String(value);
    }
  };
  return { locale, number, money, moneyCents, percent, date, dateTime, list, apply };
};
